import { Calculation, InvoicesLogic, PayrollDetails, Calculations, Translations, Configs } from "@salaxy/core";
import angular from "angular";
import { SessionService } from "../../services/SessionService";
import { UiHelpers } from "../../services/ui/UiHelpers";
/**
 * New payment controller for Salaxy Pay
 */
export class NewPaymentController implements angular.IController {
  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["SessionService", "Calculations", "UiHelpers", "$location"];

  constructor(private sessionService: SessionService, private calculations: Calculations, private uiHelpers: UiHelpers, private $location: angular.ILocationService) {
  }

  /**
   * Text for the payment button
   * This is typically customized in custom payment scenarios.
   */
  public getPaymentButtonText(label: string): string {
    return label ? Translations.get(label) : Translations.get("SALAXY.UI_Terms.pay");
  }

  /**
   * Opens the Salaxy Pay payment for a Calculation or PayrollDetails item.
   * If the model is a Calculation, it will first save the calculation before proceeding.
   * Generates a URL for Salaxy Pay and opens it in a new window.
   * @param model - The Calculation or PayrollDetails to pay.
   * @param step - Optional step number to navigate to in the payment wizard.
   */
  public openSalaxyPay(model: Calculation | PayrollDetails, step?: number): void {
    if (InvoicesLogic.isCalculation(model)) {
      this.openPaymentForCalculation(model as Calculation, step);
    } else if (InvoicesLogic.isPayroll(model)) {
      this.openPaymentForPayroll(model as PayrollDetails, step);
    } else {
      throw new Error("Invalid business object type");
    }
  }

  /**
   * Resolves the base URL for Salaxy Pay based on the current environment.
   * @returns The base URL for the payment service.
   */
  private resolveBaseUrl(): string {
    switch (Configs.current.getEnv()) {
      case "prod":
        return "https://pay.palkkaus.fi";
      case "local":
        return "http://localhost:5173";
      case "test":
        return "https://test-pay.palkkaus.fi";
      case "rc":
      case "demo":
      default:
        return "https://demo-pay.palkkaus.fi";
    }
  }

  /**
   * Validates required parameters for opening payment.
   * @param baseUrl - The base URL for the payment service.
   * @param accessToken - The access token for authentication.
   * @param businessObjectType - The type of business object.
   * @param businessObjectId - The ID of the business object.
   * @throws Error if any required parameter is missing.
   */
  private validatePaymentParams(baseUrl: string, accessToken: string, businessObjectType: string, businessObjectId: string): void {
    if (!baseUrl) {
      throw new Error("Base URL is not set");
    }
    if (!accessToken) {
      throw new Error("Access token is not set");
    }
    if (!businessObjectType) {
      throw new Error("Business object type is not set");
    }
    if (!businessObjectId) {
      throw new Error("Business object id is not set");
    }
  }

  /**
   * Opens payment for a calculation, saving it first if needed.
   * @param calculation - The calculation to pay.
   * @param step - Optional step number to navigate to in the payment wizard.
   */
  private openPaymentForCalculation(calculation: Calculation, step?: number): void {
    if (step !== undefined) {
      if (!calculation.id) {
        console.error("Error opening salaxy pay: Calculation ID is not set");
        return;
      }
      this.openPayment(calculation.id, "calculation", undefined, step);
      return;
    }
    const loading = this.uiHelpers.showLoading("Avataan maksamista...");
    this.calculations.save(calculation).then((savedCalculation) => {
      if (!savedCalculation.id) {
        throw new Error("Calculation ID is not set after saving");
      }
      this.$location.path("/calc/details/" + savedCalculation.id + "&refresh=" + new Date().getTime());
      this.openPayment(savedCalculation.id, "calculation", loading, step);
    }).catch((error) => {
      console.error("Error opening salaxy pay:", error);
      loading.dismiss();
      this.uiHelpers.showAlert("Virhe", "Jotain meni pieleen maksamisen avaamisessa.");
    });
  }

  /**
   * Opens payment for a payroll (assumes payroll is already saved).
   * @param payroll - The payroll to pay.
   * @param step - Optional step number to navigate to in the payment wizard.
   */
  private openPaymentForPayroll(payroll: PayrollDetails, step?: number): void {
    if (!payroll.id) {
      console.error("Error opening salaxy pay: Payroll ID is not set");
      return;
    }
    this.openPayment(payroll.id, "payroll", undefined, step);
  }

  /**
   * Opens the payment window with validated parameters.
   * @param businessObjectId - The ID of the business object.
   * @param businessObjectType - The type of business object.
   * @param loading - Optional loading indicator with dismiss method.
   * @param loading.dismiss - Dismiss the loading indicator.
   * @param step - Optional step number to navigate to in the payment wizard.
   */
  private openPayment(businessObjectId: string, businessObjectType: string, loading?: { dismiss: () => void }, step?: number): void {
    try {
      const baseUrl = this.resolveBaseUrl();
      const accessToken = this.sessionService.getCurrentToken();
      this.validatePaymentParams(baseUrl, accessToken, businessObjectType, businessObjectId);
      let url = `${baseUrl}/#access_token=${accessToken}&business_object_id=${businessObjectId}&business_object_type=${businessObjectType}`;
      if (step !== undefined) {
        url += `&step=${step}`;
      }
      window.open(url, "_blank", "noopener");
      if (loading) {
        loading.dismiss();
      }
      //TODO: Review this approach
      //This is one approach to handle the case when the uesr closes salaxy pay window and we want to reload the calculation details page
      //If this is good approcah, implement this also to payroll payments
      this.uiHelpers.showAlert("Ilmoitus", "Palkkalaskelman maksaminen on avattu toiseen välilehteen.").then(() => {
        this.$location.path("/calc/details/" + businessObjectId + "&refresh=" + new Date().getTime());
      });
    } catch (error) {
      console.error("Error opening salaxy pay:", error);
      if (loading) {
        loading.dismiss();
      }
      this.uiHelpers.showAlert("Virhe", "Jotain meni pieleen palkkalaskelman maksamisen aloittamisessa.");
    }
  }
}
